import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Input, Card, Tag, Typography, message, Alert, Divider, Space } from "antd";
import { EditOutlined, InfoCircleOutlined, WarningOutlined, FileTextOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;
const { TextArea } = Input;

const AddNoteModal = ({ 
  visible, 
  task, 
  onClose, 
  onNoteAdded,
  currentUser,
  apiService,
  refreshPage // Add this prop
}) => {
  const [noteText, setNoteText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [existingNote, setExistingNote] = useState("");

  // Load existing note when modal opens
  useEffect(() => {
    if (visible && task) {
      const existingManagerNote = task.managerNote || task.originalData?.managerNote || "";
      setExistingNote(existingManagerNote);
      setNoteText("");
    }
  }, [visible, task]);

  const handleAddNote = async () => {
    if (!noteText.trim()) {
      message.warning("Please enter a note");
      return;
    }

    setSubmitting(true);
    try {
      const formData = JSON.stringify({
        managerNote: noteText,
        frequency: task.frequency,
        tenantDTO: {
          id: currentUser?.tenantId,
          tenantApiKey: currentUser?.tenantApiKey,
        }
      });
      
      const response = await apiService.TasksAddManagerNote(task.id, formData);
      
      if (response) {
        message.success(
          existingNote 
            ? "Note updated successfully for task: " + task.taskName 
            : "Note added successfully for task: " + task.taskName
        );
        
        if (onNoteAdded) {
          onNoteAdded(task.id, {
            taskId: task.id,
            managerNote: noteText,
            frequency: task.frequency,
            addedBy: currentUser?.sub,
            addedByName: currentUser?.name || currentUser?.sub,
            timestamp: new Date().toISOString(),
            taskName: task.taskName,
            department: task.department,
            area: task.area,
            status: task.status
          });
        }
        
        setNoteText("");
        onClose();
        
        // Refresh the page after note is added
        if (refreshPage) {
          refreshPage();
        } else {
          // Fallback: refresh the page
          window.location.reload();
        }
      }
    } catch (err) {
      console.error("Error adding note:", err);
      message.error(err?.message || "Failed to add note");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setNoteText("");
    setExistingNote("");
    onClose();
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ 
            width: 32, 
            height: 32, 
            borderRadius: '50%', 
            background: existingNote ? '#fa8c16' : '#1890ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <EditOutlined style={{ color: 'white', fontSize: 16 }} />
          </div>
          <span style={{ fontSize: 18, fontWeight: 600 }}>
            {existingNote ? "Update Manager Note" : "Add Manager Note"}
          </span>
        </div>
      }
      open={visible}
      onCancel={handleClose}
      footer={[
        <Button key="cancel" onClick={handleClose} size="large">
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleAddNote}
          loading={submitting}
          size="large"
          style={{ 
            backgroundColor: existingNote ? '#fa8c16' : '#1890ff',
            borderColor: existingNote ? '#fa8c16' : '#1890ff'
          }}
        >
          {existingNote ? "Update Note" : "Add Note"}
        </Button>,
      ]}
      width={700}
    >
      {task && (
        <div>
          {/* Task Details Card */}
          <Card 
            style={{ 
              marginBottom: 24, 
              borderRadius: 12,
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
            }}
            bodyStyle={{ padding: '16px' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: 12 }}>
              <FileTextOutlined style={{ color: '#1890ff', fontSize: 18 }} />
              <Text strong style={{ fontSize: 15 }}>Task Information</Text>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
              <div style={{ flex: '1', minWidth: '150px' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Task Name</Text>
                <div>
                  <Text strong style={{ fontSize: 14 }}>{task.taskName}</Text>
                  {task.priority === "high" && task.status !== "completed" && (
                    <Tag color="red" size="small" style={{ marginLeft: 8 }}>High Priority</Tag>
                  )}
                </div>
              </div>
              <div style={{ flex: '1', minWidth: '120px' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Department</Text>
                <div>
                  <Tag color="geekblue" style={{ marginTop: 4 }}>{task.department || "N/A"}</Tag>
                </div>
              </div>
              <div style={{ flex: '1', minWidth: '120px' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Area</Text>
                <div>
                  <Tag color="cyan" style={{ marginTop: 4 }}>{task.area || "N/A"}</Tag>
                </div>
              </div>
              <div style={{ flex: '1', minWidth: '100px' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Status</Text>
                <div>
                  <Tag 
                    color={task.status === "completed" ? "green" : "orange"} 
                    style={{ marginTop: 4 }}
                  >
                    {task.status?.toUpperCase() || "PENDING"}
                  </Tag>
                </div>
              </div>
              {task.frequency && (
                <div style={{ flex: '1', minWidth: '100px' }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>Frequency</Text>
                  <div>
                    <Tag 
                      color={task.frequency === "DAILY" ? "blue" : task.frequency === "WEEKLY" ? "green" : "orange"}
                      style={{ marginTop: 4 }}
                    >
                      {task.frequency}
                    </Tag>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Current Note Section - Display existing note prominently */}
          {existingNote && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ 
                background: '#fff7e6', 
                borderRadius: 12,
                border: '1px solid #ffd591',
                overflow: 'hidden'
              }}>
                <div style={{ 
                  background: '#fa8c16', 
                  padding: '12px 16px',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <WarningOutlined style={{ fontSize: 16 }} />
                  <Text strong style={{ color: 'white', fontSize: 14 }}>Current Note (Will be replaced)</Text>
                </div>
                <div style={{ padding: '16px' }}>
                  <div style={{ 
                    background: 'white', 
                    padding: '16px', 
                    borderRadius: 8,
                    border: '1px solid #ffe7ba'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                      <div style={{ 
                        width: 4, 
                        height: 'auto', 
                        background: '#fa8c16', 
                        borderRadius: 2,
                        minHeight: 40
                      }} />
                      <div style={{ flex: 1 }}>
                        <Text style={{ fontSize: 14, lineHeight: 1.6 }}>
                          {existingNote}
                        </Text>
                        <div style={{ marginTop: 12 }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            <InfoCircleOutlined /> This note will be permanently replaced when you add a new note
                          </Text>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <Divider style={{ margin: '16px 0' }}>
            <Text type="secondary">
              {existingNote ? "Enter New Note" : "Enter Note"}
            </Text>
          </Divider>

          {/* Add/Update Note Section */}
          <Form layout="vertical">
            <Form.Item
              label={
                <span style={{ fontSize: 14, fontWeight: 500 }}>
                  {existingNote ? "New Note" : "Manager Note"}
                </span>
              }
              required
              tooltip="Add your observation or comment about this task"
              extra={
                <span style={{ fontSize: 12 }}>
                  Maximum 500 characters. {existingNote ? "This will replace the existing note." : "This note will be saved to this task."}
                </span>
              }
            >
              <TextArea
                rows={5}
                placeholder="Enter your note here... (e.g., Quality check observations, issues found, recommendations, completion notes, etc.)"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                showCount
                maxLength={500}
                autoFocus
                style={{ fontSize: 14, borderRadius: 8 }}
              />
            </Form.Item>
          </Form>

          {/* Warning Message for Replacement */}
          {existingNote && (
            <Alert
              message="⚠️ Warning: Replacement in Progress"
              description="Adding a new note will permanently replace the existing note. This action cannot be undone. Please make sure you want to update the note before proceeding."
              type="warning"
              showIcon
              style={{ 
                marginTop: 16, 
                borderRadius: 8,
                background: '#fff7e6',
                borderColor: '#ffd591'
              }}
            />
          )}

          {/* Info Message for New Note */}
          {!existingNote && (
            <Alert
              message="💡 Note Information"
              description="Each task can have only one manager note. Once added, you can update it later if needed."
              type="info"
              showIcon
              style={{ 
                marginTop: 16, 
                borderRadius: 8,
                background: '#e6f7ff',
                borderColor: '#91d5ff'
              }}
            />
          )}
        </div>
      )}
    </Modal>
  );
};

export default AddNoteModal;